/*
 * consultant-bff.js — shared client helper for the LIVE consultant dashboard surfaces
 * (ANN-496 / consultant hub). Binds the Shopify App-Proxy BFF endpoints into the existing
 * consultant-app design system (assets/consultant-app.css). Dependency-free, ES5, exposes a
 * single `window.ConsultantBff` namespace. Mirrors the fetch/state approach already proven in
 * sections/consultant-manage-downline.liquid, sections/login-bar.liquid and assets/dbm-price-ladder.js.
 *
 * The storefront calls the App Proxy at /apps/consultant/api/extension/<path> with
 * {credentials:'include'} — Shopify adds the HMAC + logged_in_customer_id server-side. The theme
 * NEVER computes an HMAC. Responses may carry snake_case (TS client contract) OR camelCase
 * (default .NET JSON) keys, so every read goes through pick().
 */
(function () {
  'use strict';

  var EXT = '/apps/consultant/api/extension';

  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // ---- field access --------------------------------------------------------------------------
  // Tolerate snake_case AND camelCase — return the first non-null of the two keys.
  function pick(o, a, b) { return (o && (o[a] != null ? o[a] : o[b])); }

  function esc(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ---- currency ------------------------------------------------------------------------------
  // en-ZA ZAR renders as 'R 17 641,46' (narrow/no-break group space, comma decimal) — matches the
  // theme's existing money strings. Never emits a fabricated figure: a non-numeric value -> '—'.
  var _zar = null;
  function zarFormatter() {
    if (_zar) return _zar;
    try {
      _zar = new Intl.NumberFormat('en-ZA', {
        style: 'currency', currency: 'ZAR',
        minimumFractionDigits: 2, maximumFractionDigits: 2
      });
    } catch (e) { _zar = null; }
    return _zar;
  }
  function money(v) {
    var n = parseFloat(v);
    if (!isFinite(n)) return '—'; // em dash — honest "no value"
    var f = zarFormatter();
    // Drop the space between the R symbol and the amount (R 559,20 -> R559,20); group spaces kept.
    if (f) return f.format(n).replace(/(R)\s+(?=\d)/, '$1');
    // Fallback if Intl/ZAR unavailable: R 1 234,56 shape.
    var neg = n < 0; n = Math.abs(n);
    var intPart = Math.floor(n);
    var frac = Math.round((n - intPart) * 100);
    if (frac === 100) { intPart += 1; frac = 0; }
    var intStr = String(intPart).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return (neg ? '-' : '') + 'R ' + intStr + ',' + (frac < 10 ? '0' + frac : frac);
  }

  function monthLabel(m, y) {
    var mi = parseInt(m, 10);
    var label = (mi >= 1 && mi <= 12) ? MONTHS[mi - 1] : '';
    return (label + ' ' + (y == null ? '' : y)).trim();
  }

  // ClickUp 869f5ytkm — the month a person in South Africa is in RIGHT NOW (1-12), from an instant
  // (ms since epoch; defaults to Date.now()). SAST is UTC+2 with no DST, so between 00:00 and 02:00
  // SAST on the 1st the UTC month — and a browser set to another zone — is still on last month.
  function sastMonth(nowMs) {
    var t = (nowMs == null) ? Date.now() : nowMs;
    try {
      var m = new Intl.DateTimeFormat('en-ZA', { month: 'numeric', timeZone: 'Africa/Johannesburg' }).format(new Date(t));
      var n = parseInt(m, 10);
      if (n >= 1 && n <= 12) return n;
    } catch (e) { /* no tz data — fall through to the fixed +2h offset, exact for SAST */ }
    return new Date(t + 2 * 3600 * 1000).getUTCMonth() + 1;
  }

  // ClickUp 869f5ytkm — the label for the month a /reports/vds statement covers. The server sets
  // period_status: 'open' = AM's running month (figures are month-to-date, nothing paid yet);
  // anything else = a finalised statement. Every dashboard tile fed by the statement names THIS month,
  // so last month's figures are never passed off as this month's.
  function statementPeriodLabel(d) {
    var period = pick(d, 'period', 'period') || {};
    var month = monthLabel(pick(period, 'month', 'month'), pick(period, 'year', 'year'));
    var open = pick(d, 'period_status', 'periodStatus') === 'open';
    // as_of is AM's dStatement, a zone-less SAST wall-clock date ('2026-10-01T00:00:00'). Read only its
    // DATE part: parsed whole, a browser in SAST takes it as local midnight and dateLabel's UTC read
    // prints the day before (30 September for a 1 October statement).
    var asOfRaw = pick(d, 'as_of', 'asOf');
    var asOfDay = (asOfRaw && /^\d{4}-\d{2}-\d{2}/.test(String(asOfRaw))) ? String(asOfRaw).slice(0, 10) : asOfRaw;
    return {
      month: month || '—',
      open: open,
      caption: open
        ? (month || '—') + ' — month to date, still in progress'
        : (month || '—') + ' — final statement (as of ' + dateLabel(asOfDay) + ')',
      totalPaidLabel: open
        ? 'Total Paid (VAT inclusive) — ' + (month || '—') + ' so far, paid after month-end'
        : 'Total Paid (VAT inclusive) — ' + (month || '—')
    };
  }

  // Format an ISO date (YYYY-MM-DD or full ISO) as e.g. '3 July 2026'; blank/invalid -> '—'.
  function dateLabel(iso) {
    if (!iso) return '—';
    var d = new Date(iso);
    if (isNaN(d.getTime())) {
      // Accept a bare YYYY-MM-DD without time (Safari-safe).
      var m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return '—';
      d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
      if (isNaN(d.getTime())) return '—';
    }
    return d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()] + ' ' + d.getUTCFullYear();
  }

  // ---- fetch ---------------------------------------------------------------------------------

  // Retry-After -> milliseconds. The header is either delta-seconds ('5') or an HTTP-date; an absent,
  // empty or unreadable header is null, meaning "the server did not say". Never negative — a date
  // already in the past means "now".
  function retryAfterMs(res) {
    var raw = null;
    try {
      if (res && res.headers && typeof res.headers.get === 'function') raw = res.headers.get('Retry-After');
    } catch (e) { raw = null; }
    if (raw == null) return null;
    var s = String(raw).trim();
    if (!s) return null;
    if (/^\d+$/.test(s)) { var secs = parseInt(s, 10); return isFinite(secs) ? secs * 1000 : null; }
    var when = Date.parse(s);
    if (isNaN(when)) return null;
    var ms = when - Date.now();
    return ms > 0 ? ms : 0;
  }

  /*
   * envelope — the ONE result shape every BFF call resolves to:
   *   { ok, status, data, parsed, retryAfter }
   *
   * parsed     : FALSE when the body could not be read as JSON. `data` is still {} so no caller ever
   *              throws on r.data.x — but "the body was unreadable" and "the body was an empty object"
   *              are NOT the same fact, and a caller that would otherwise overwrite known-good state
   *              with {} has to be able to tell them apart. An unreadable body is not an answer, and
   *              this flag is the only way for a caller to know that.
   * retryAfter : the server's Retry-After in ms, or null. A 503 + Retry-After is "ask again shortly",
   *              which is a different thing from a failure — see the order-status poller.
   */
  function envelope(res) {
    var parsed = true;
    return res.json()
      .catch(function () { parsed = false; return {}; })
      .then(function (data) {
        return { ok: res.ok, status: res.status, data: data, parsed: parsed, retryAfter: retryAfterMs(res) };
      });
  }

  function fetchBff(path, headers) {
    var h = { Accept: 'application/json' };
    if (headers) { for (var k in headers) { if (Object.prototype.hasOwnProperty.call(headers, k)) h[k] = headers[k]; } }
    return fetch(EXT + path, { method: 'GET', credentials: 'include', headers: h }).then(envelope);
  }

  // send — the mutating sibling of fetchBff (POST/PUT/PATCH/DELETE). The App Proxy signs every
  // method + injects logged_in_customer_id, so a credentialed fetch authenticates without an HMAC.
  // `body` is JSON-encoded when present; bodyless mutations (DELETE, set-default) omit it. Same
  // envelope as fetchBff.
  function send(path, method, body, headers) {
    var init = { method: method, credentials: 'include', headers: { Accept: 'application/json' } };
    if (body != null) { init.headers['Content-Type'] = 'application/json'; init.body = JSON.stringify(body); }
    if (headers) { for (var k in headers) { if (Object.prototype.hasOwnProperty.call(headers, k)) init.headers[k] = headers[k]; } }
    return fetch(EXT + path, init).then(envelope);
  }

  function show(el) { if (el) el.style.display = ''; }
  function hide(el) { if (el) el.style.display = 'none'; }

  // ---- state machine -------------------------------------------------------------------------
  // els: { region, loading, error, empty, data }. state: 'loading'|'error'|'empty'|'data'.
  // region carries aria-busy so assistive tech announces the async transition.
  function setState(els, state) {
    hide(els.loading); hide(els.error); hide(els.empty); hide(els.data);
    if (els.region) els.region.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    if (state === 'loading') show(els.loading);
    else if (state === 'error') show(els.error);
    else if (state === 'empty') show(els.empty);
    else show(els.data);
  }

  /*
   * voucherGives — what a voucher GIVES, as one short phrase.
   *
   * MUST match assets/dbm-vouchers.js `gives()` (the cart picker). The consultant sees both, and a
   * disagreement between them is a real bug: the Rewards page shipped 2026-08-10 money-formatting a
   * PERCENT, so a 20%-off voucher read "R0,20". `value` is a FRACTION for percent (0.20 = 20%), which
   * is exactly why blind money() formatting looks plausible and is wrong.
   *
   * tests/consultant-rewards-vouchers.test.mjs pins the two implementations to the same outputs.
   */
  function voucherGives(v) {
    if (!v) { return ''; }
    if (v.type === 'percent' && v.value != null) { return Math.round(Number(v.value) * 100) + '% off'; }
    if (v.type === 'amount' && v.value != null) { return money(v.value) + ' off'; }
    if (v.type === 'free_shipping') { return 'Free shipping'; }
    if (v.type === 'free_product') { return 'Free product'; }
    return '';
  }

  /*
   * mountAsync — the loading -> error(+retry) -> empty -> data pipeline for a single region.
   * opts:
   *   els     : { region, loading, error, empty, data }
   *   retry   : optional <button> that re-runs load
   *   load    : function() -> Promise<{ok,status,data}>   (usually fetchBff(path))
   *   isEmpty : function(data) -> bool
   *   render  : function(data)                            (paint into els.data)
   *   isGone  : function(r) -> bool    OPTIONAL. Judged on the RESPONSE, not the data. True hides
   *             els.region entirely instead of showing an error. For a feature-gated endpoint a 404
   *             means "this feature is off in this environment", which is not a fault the consultant
   *             can retry — rendering "we could not load" would be a lie about a working system.
   * Returns { reload }.
   */
  function mountAsync(opts) {
    var els = opts.els || {};
    function run() {
      setState(els, 'loading');
      opts.load()
        .then(function (r) {
          if (opts.isGone && opts.isGone(r)) {
            if (els.region) { els.region.style.display = 'none'; }
            return;
          }
          if (!r || !r.ok) { setState(els, 'error'); return; }
          if (opts.isEmpty && opts.isEmpty(r.data)) { setState(els, 'empty'); return; }
          try { opts.render(r.data); } catch (e) { setState(els, 'error'); return; }
          setState(els, 'data');
        })
        .catch(function () { setState(els, 'error'); });
    }
    // Bind the retry click ONCE per element. mountAsync can legitimately be called again against the
    // SAME retry button (e.g. this section calls loadProfile()/mountAsync repeatedly — on verify
    // success, on every Save/Cancel, on a 403) — without this guard every such call would add
    // another 'click' listener onto the same node, so a single retry click after a few round-trips
    // would fire run() N times instead of once. A plain property flag works for both a real DOM
    // element and a test stub, and needs no cleanup since the element outlives the page.
    if (opts.retry && !opts.retry.__dbmMountAsyncBound) {
      opts.retry.__dbmMountAsyncBound = true;
      opts.retry.addEventListener('click', run);
    }
    run();
    return { reload: run };
  }

  // ---- consultant-status gate ----------------------------------------------------------------
  // Belt-and-suspenders (login-bar parity): even when Liquid rendered the live surface, probe the
  // session so a stale/cached context can't leak fabricated data to a guest / non-consultant.
  var STATUS_KEY = 'dbm_consultant_status';

  // ---- unified eligibility map (Lane-2 "SHOW", 2026-07-15 handover §3.1) -----------------------
  // GET /consultant-status is being extended (same endpoint, same probe) to the blessed superset:
  //   { signed_in, is_consultant,
  //     exclusive: [{ sku, handle, qty_remaining }],   // this consultant's entitled exclusive items
  //     bmtm: { eligible } | has_bmtm,                 // Try-Me eligibility
  //     vouchers: { count, total }, gwp: { eligible, offers: [...] } }
  // Every field beyond signed_in/is_consultant is OPTIONAL — a DBM build that predates the map
  // returns only the two flags, so every reader MUST degrade (absent => empty/false). Cached in
  // sessionStorage stale-while-revalidate with a ~3-minute TTL (the blessed 2-5 min window): a
  // just-consumed entitlement may linger for a few minutes — harmless, the checkout Function stays
  // the sole enforcement layer. Display ≠ enforcement.
  var MAP_KEY = 'dbm_consultant_status_v2'; // JSON envelope { t: epoch-ms, v: map }
  var MAP_TTL_MS = 3 * 60 * 1000;
  var _mapInflight = null;

  function readMapCache() {
    try {
      var raw = sessionStorage.getItem(MAP_KEY);
      if (!raw) return null;
      var env = JSON.parse(raw);
      if (!env || typeof env !== 'object' || env.v == null) return null;
      return env;
    } catch (e) { return null; }
  }

  // TRUE when two maps disagree on WHO the session is (signed_in / is_consultant) — a logout or an
  // account switch in the same tab. Entitlement payloads (exclusive[], gwp, …) are identity-keyed,
  // so an identity flip invalidates the whole cached map, not just the two booleans.
  function identityChanged(prev, next) {
    if (!prev || !next) return false;
    return (!!pick(prev, 'signed_in', 'signedIn') !== !!pick(next, 'signed_in', 'signedIn'))
      || (!!pick(prev, 'is_consultant', 'isConsultant') !== !!pick(next, 'is_consultant', 'isConsultant'));
  }

  function writeMapCache(v) {
    try {
      // Belt-and-braces (cross-identity stale-cache review, 2026-07-15): when the LIVE answer
      // disagrees with the cache on identity, REMOVE both keys before writing — if the setItem
      // below then fails (quota), the failure mode is "no cache" (honest re-probe) instead of
      // "the previous identity's entitlements survive under a fresh TTL".
      var env = readMapCache();
      if (env && identityChanged(env.v, v)) {
        sessionStorage.removeItem(MAP_KEY);
        sessionStorage.removeItem(STATUS_KEY);
      }
      sessionStorage.setItem(MAP_KEY, JSON.stringify({ t: Date.now(), v: v }));
      // Legacy readers (login-bar inline script, guard) keep reading the raw response under the
      // original key — the map is a superset of { signed_in, is_consultant }, so both stay valid.
      sessionStorage.setItem(STATUS_KEY, JSON.stringify(v));
    } catch (e) {}
  }

  // Single-flight live probe: concurrent surfaces on one page share one network request.
  function fetchStatusMap() {
    if (_mapInflight) return _mapInflight;
    _mapInflight = fetchBff('/consultant-status')
      .then(function (r) {
        _mapInflight = null;
        if (!r || !r.ok || r.data == null) return null;
        writeMapCache(r.data);
        return r.data;
      })
      .catch(function () { _mapInflight = null; return null; });
    return _mapInflight;
  }

  /*
   * eligibility(opts) -> { cached, refresh } — the ONE probe helper every gated surface uses.
   *   cached  : last-seen map (ANY age; null when never fetched). Paint optimistically from this.
   *   refresh : Promise<map|null> — the freshest answer. Default (TTL mode): the cache itself when
   *             younger than the TTL, otherwise a live re-fetch. With opts.live === true the TTL is
   *             BYPASSED — refresh is ALWAYS a live probe (single-flight, so concurrent surfaces on
   *             one page still share one request); it resolves with the stale cache when the network
   *             fails, null when there is nothing at all. Never rejects.
   *
   * WHICH MODE (cross-identity stale-cache review, 2026-07-15): a PERSONALIZED surface — one that
   * renders identity-keyed map data (exclusive[] cards, quantities, add-to-cart) — MUST pass
   * { live: true }: sessionStorage survives logout/account-switch in the same tab, so a TTL-fresh
   * cache can carry the PREVIOUS identity's entitlements and, without a live leg, no live negative
   * ever arrives to tear the paint down. Boolean reveal gates (nav item, hub card, Try-Me un-gate)
   * may ride the TTL — worst case they briefly reveal a SHARED surface, and the login-bar's
   * every-page live probe rewrites the cache within one round-trip.
   *
   * Callers paint from `cached` (no flash), then reconcile from `refresh` — mirroring guard():
   * only a LIVE negative may tear a surface down; a stale cache must never fabricate access.
   */
  function eligibility(opts) {
    var env = readMapCache();
    var cached = env ? env.v : null;
    var live = !!(opts && opts.live);
    var fresh = !live && env != null && (Date.now() - env.t) < MAP_TTL_MS;
    return {
      cached: cached,
      refresh: fresh
        ? Promise.resolve(cached)
        : fetchStatusMap().then(function (v) { return v != null ? v : cached; })
    };
  }

  // exclusive[] — entitled exclusive items ({ sku, handle, qty_remaining }); absent/empty => [].
  function exclusiveOf(map) {
    var v = pick(map, 'exclusive', 'exclusive');
    return (v && v.length) ? v : [];
  }

  // Try-Me eligibility — tolerate the nested { bmtm: { eligible } } shape AND flat has_bmtm/hasBmtm.
  function bmtmEligibleOf(map) {
    if (!map) return false;
    var b = pick(map, 'bmtm', 'bmtm');
    if (b && pick(b, 'eligible', 'eligible')) return true;
    return !!pick(map, 'has_bmtm', 'hasBmtm');
  }

  // Business Tools eligibility (ANN-592) — consultant-only collateral (price lists, brochure packs,
  // order books, badges). Same nested/flat tolerance as bmtmEligibleOf: the probe returns
  // { business_tools: { eligible } }. Unlike Try-Me there is NO per-SKU entitlement list — every
  // consultant may buy every business tool — so this is a single audience boolean, and the server
  // derives it from the SAME dbm.tier the checkout gate reads (so the reveal and the gate can never
  // disagree about who is a consultant).
  function businessToolsEligibleOf(map) {
    if (!map) return false;
    var b = pick(map, 'business_tools', 'businessTools');
    if (b && pick(b, 'eligible', 'eligible')) return true;
    return !!pick(map, 'has_business_tools', 'hasBusinessTools');
  }

  function gateHtml(kind, returnPath) {
    if (kind === 'guest') {
      return '<div class="ca-card"><div class="ca-gate ca-vstack ca-gap-3">' +
        '<h1 class="ca-h2">Consultant sign in</h1>' +
        '<p class="ca-sub">Please sign in with your Annique account to view this area.</p>' +
        '<a class="ca-btn" href="/customer_authentication/login?return_to=' + encodeURIComponent(returnPath || '/') + '">Sign in</a>' +
        '</div></div>';
    }
    if (kind === 'activating') {
      // Sheet 46 — the interim window between activation and the projection landing.
      //
      // She has just been activated: AccountMate has her, and DBM has seeded her projection row.
      // What has NOT happened yet is the sweep (or the real-time consumer) that puts the consultant
      // tag and her tier metafields on the Shopify customer. Until then every gated surface reads
      // "not a consultant".
      //
      // The old copy told her "your account isn't linked to a consultant number yet", which is both
      // alarming and untrue — she IS linked. This says what is actually happening, and that it fixes
      // itself. No sign-in link and no back-to-store dead end: she has nothing to fix.
      return '<div class="ca-card"><div class="ca-gate ca-vstack ca-gap-3">' +
        '<h1 class="ca-h2">Your access is being set up</h1>' +
        '<p class="ca-sub">Welcome! We are finishing your consultant account now. This usually takes ' +
        'a few minutes — refresh this page shortly and it will be ready.</p>' +
        '<button type="button" class="ca-btn" onclick="window.location.reload()">Refresh</button>' +
        '</div></div>';
    }
    return '<div class="ca-card"><div class="ca-gate ca-vstack ca-gap-3">' +
      '<h1 class="ca-h2">Consultants only</h1>' +
      '<p class="ca-sub">This area is for Annique consultants. Your account isn’t linked to a consultant number yet.</p>' +
      '<a class="ca-btn ca-btn--outline" href="/">Back to store</a>' +
      '</div></div>';
  }

  /*
   * guard(opts) — probe /consultant-status and route.
   *   surfaceEl    : the live-surface container; replaced with a gate card when the session is
   *                  not a signed-in consultant.
   *   returnPath   : path to return to after sign-in.
   *   onConsultant : called once the session confirms a signed-in consultant.
   *   serverGated  : pass TRUE only when the surrounding Liquid ALREADY gated this surface on the
   *                  Shopify `annique-consultant` customer tag for THIS request — i.e. the markup
   *                  guard() is protecting could not have been rendered for a non-consultant at all.
   *
   * Uses stale-while-revalidate: a cached positive status runs onConsultant immediately (no flash),
   * then revalidates. A negative live result swaps in the gate.
   *
   * WHY serverGated EXISTS (order-status money-path review, 2026-08-03)
   * ------------------------------------------------------------------
   * /consultant-status DEGRADES a failed projection read to 200 { signed_in:true, is_consultant:false }.
   * On the wire that is byte-identical to a real non-consultant, so guard() took the destructive
   * branch and replaced a just-paid consultant's order status with "Consultants only" — and, because
   * the teardown happens instead of onConsultant, the page never even started polling.
   *
   * The two cases are separated by the OTHER authority in the room, not by the probe:
   *
   *   • signed_in FALSE — still authoritative for every caller, serverGated or not. DBM derives it
   *     from the App Proxy's logged_in_customer_id, which involves no projection read, so the
   *     degradation cannot manufacture it. This is the leak guard() actually exists for (a cached
   *     consultant page served to a signed-out visitor) and nothing below relaxes it.
   *
   *   • signed_in TRUE + is_consultant FALSE on a serverGated surface — this CONTRADICTS Shopify's own
   *     customer record, which was read server-side on this very request (not from client cache). One
   *     of the two authorities is known to fail closed under load; the other is not. So the honest
   *     reading is "we could not tell", not "no" — and we do not take an irreversible action on an
   *     uncertain answer. The surface stays and onConsultant runs.
   *
   *     This does not weaken the gate for a real non-consultant, because on a server-gated surface
   *     there is no real non-consultant to weaken it for: Liquid rendered the gate card, not this
   *     markup, for anyone without the tag. And every datum the surface then loads still comes from a
   *     per-request, server-authorised BFF call that answers 403/404 to anyone not entitled to it.
   *     guard() is a reveal gate; the BFF is the gate.
   *
   *   • Without serverGated, every other caller behaves byte-for-byte as before.
   */
  function guard(opts) {
    var surfaceEl = opts.surfaceEl;
    var returnPath = opts.returnPath || (window.location && window.location.pathname) || '/';
    var serverGated = !!opts.serverGated;
    var started = false;
    function startOnce() { if (started) return; started = true; if (opts.onConsultant) opts.onConsultant(); }
    function apply(s, live) {
      if (!s) return;
      var signedIn = pick(s, 'signed_in', 'signedIn');
      var isConsultant = pick(s, 'is_consultant', 'isConsultant');
      if (signedIn && isConsultant) { startOnce(); return; }
      // "Signed in, but not a consultant" is the ONE negative this probe can invent out of a failed
      // projection read. On a server-gated surface that makes it uncertain, not a No.
      if (signedIn && serverGated) { startOnce(); return; }
      // Only a LIVE negative may tear down the surface (a stale cache must not).
      if (live && surfaceEl) {
        // Sheet 46 — a freshly activated consultant is NOT a stranger. The server tells us apart:
        // activation_pending means her projection row is seeded but not yet projected, so the
        // honest answer is "we are still setting this up", never "you are not a consultant".
        var pending = signedIn && pick(s, 'activation_pending', 'activationPending') === true;
        surfaceEl.innerHTML = gateHtml(
          !signedIn ? 'guest' : (pending ? 'activating' : 'nonconsultant'), returnPath);
      }
    }
    // Optimistic: a cached positive lets the data load without waiting on the probe.
    try {
      var cached = sessionStorage.getItem(STATUS_KEY);
      if (cached) apply(JSON.parse(cached), false);
    } catch (e) {}

    // Route through the shared single-flight map probe (also warms the Lane-2 eligibility cache).
    fetchStatusMap()
      .then(function (v) {
        if (v == null) { startOnce(); return; } // probe failed open — Liquid already gated server-side
        apply(v, true);
      })
      .catch(function () { startOnce(); }); // network error — trust the server-side Liquid gate
  }

  // status tone → ca-status tone class.
  //
  // The status vocabulary the orders API actually emits is a KNOWN, closed set (see the order-history
  // reader's ToApiStatus), so match it exactly first. The loose regex below is only a fallback for a
  // value we don't recognise.
  //
  // Why the exact map exists: the old code was regex-only, and `/fulfil/` matches the "fulfil" INSIDE
  // "unfulfilled" — so an UNFULFILLED order scored as success and rendered GREEN, reading as though it
  // had already shipped. `partially_delivered` had the same flaw via `deliver`. Substring tests can't
  // express "not", which is exactly what these two statuses mean.
  var STATUS_TONES = {
    delivered: 'ca-tone-success',
    fulfilled: 'ca-tone-success',
    partially_delivered: 'ca-tone-info',   // some of it moved — in progress, not done
    unfulfilled: 'ca-tone-warning',        // nothing has shipped yet — needs attention
    on_hold: 'ca-tone-warning',
    scheduled: 'ca-tone-info',
    unknown: ''
  };
  function statusTone(status) {
    var s = String(status || '').toLowerCase().replace(/[\s-]+/g, '_');
    if (Object.prototype.hasOwnProperty.call(STATUS_TONES, s)) return STATUS_TONES[s];
    // Fallback for an unrecognised value. Order matters: the negative/partial cases are tested BEFORE
    // the success rule so they can never be swallowed by a substring of a positive word.
    if (/^un|not_?fulfil|unfulfil/.test(s)) return 'ca-tone-warning';
    if (/partial/.test(s)) return 'ca-tone-info';
    if (/cancel|fail|refund|reject|error/.test(s)) return 'ca-tone-danger';
    if (/deliver|complete|paid|success|fulfil/.test(s)) return 'ca-tone-success';
    if (/courier|ship|transit|dispatch|invoiced/.test(s)) return 'ca-tone-info';
    if (/pending|await|process|pick|hold/.test(s)) return 'ca-tone-warning';
    return '';
  }

  window.ConsultantBff = {
    voucherGives: voucherGives,
    EXT: EXT,
    MONTHS: MONTHS,
    pick: pick,
    esc: esc,
    money: money,
    monthLabel: monthLabel,
    dateLabel: dateLabel,
    sastMonth: sastMonth,
    statementPeriodLabel: statementPeriodLabel,
    statusTone: statusTone,
    fetchBff: fetchBff,
    send: send,
    show: show,
    hide: hide,
    setState: setState,
    mountAsync: mountAsync,
    guard: guard,
    gateHtml: gateHtml,
    eligibility: eligibility,
    exclusiveOf: exclusiveOf,
    bmtmEligibleOf: bmtmEligibleOf,
    businessToolsEligibleOf: businessToolsEligibleOf
  };
})();
